import { supabase } from './supabaseClient';
import { chunkLockQueryIds, pickLockQueryIds, ProjectLock } from './projectLockLogic';

/**
 * Duenne Schicht ueber die drei RPCs aus supabase/project_locks.sql
 * (MIGRATIONS.md Nr. 33). Hier steht ausschliesslich das Reden mit der
 * Datenbank; jede Regel — abgelaufen, fremd, Anzeigetext — steht in
 * projectLockLogic.ts und ist dort ohne Netz testbar.
 *
 * ─── Warum hier NICHTS nach oben wirft ─────────────────────────────────────
 * Die Sperre ist der EINZIGE Schutz vor Parallelbearbeitung: den Konflikt-Anker
 * beim Speichern gibt es seit dem Ausbau der optimistischen Sperre nicht mehr,
 * `saveProjectToCloud` schreibt bedingungslos („letzter Schreiber gewinnt").
 * Trotzdem — oder gerade deshalb — wird hier nichts geworfen: die Sperre ist
 * organisatorisch, nicht technisch. Ein Sperrfehler darf NIEMALS das Oeffnen
 * eines Projekts verhindern —
 * am wenigsten offline: die App hat eine echte Offline-Queue in IndexedDB und
 * ist ohne Netz voll arbeitsfaehig, waehrend jeder RPC-Aufruf scheitert. Auch
 * eine noch nicht ausgefuehrte Migration faellt in diese Klasse (PostgREST
 * antwortet dann mit `PGRST202`). Fehler landen als `console.warn` im
 * Protokoll und als `{ acquired: true, lock: null }` beim Aufrufer: „keine
 * Sperre bekannt, weitermachen".
 *
 * Einzige bewusste Ausnahme ist `takeoverProjectLock` — Begruendung dort.
 */

export interface ProjectLockResult {
    /** Darf der Aufrufer arbeiten? Bei Fehlern absichtlich true (fail-open). */
    acquired: boolean;
    /** Die geltende Sperrzeile, auch wenn sie jemand anderem gehoert. */
    lock: ProjectLock | null;
}

/** Ergebnis, wenn die Sperre nicht ermittelt werden konnte. */
const FAIL_OPEN: ProjectLockResult = { acquired: true, lock: null };

/**
 * snake_case aus der Datenbank nach camelCase.
 *
 * Die RPCs geben eine Tabelle zurueck; supabase-js liefert das als Array mit
 * genau einer Zeile. Eine leere Antwort ist ein gueltiger Fall (die Sperre war
 * im selben Augenblick weg) und bedeutet „keine Zeile", nicht „Fehler".
 */
const mapLockRow = (row: any): ProjectLock | null => {
    if (!row || !row.project_id || !row.user_id) return null;
    return {
        projectId: row.project_id,
        userId: row.user_id,
        userLabel: row.user_label ?? null,
        acquiredAt: row.acquired_at,
        heartbeatAt: row.heartbeat_at,
    };
};

/** Erste Zeile einer RPC-Antwort — egal ob Array oder Einzelobjekt. */
const firstRow = (data: any): any => (Array.isArray(data) ? data[0] : data);

const callLockRpc = async (
    fn: 'acquire_project_lock' | 'takeover_project_lock',
    params: Record<string, unknown>,
): Promise<{ row: any } | { error: unknown }> => {
    const { data, error } = await supabase.rpc(fn, params);
    if (error) return { error };
    return { row: firstRow(data) };
};

/**
 * Nimmt die Sperre fuer ein Projekt — oder meldet, wer sie haelt.
 *
 * `acquired === false` ist die einzige Aussage, die den Aufrufer bremsen darf;
 * `lock` benennt dann den Halter fuer das Hinweisbanner.
 */
export const acquireProjectLock = async (
    projectId: string,
    label?: string | null,
): Promise<ProjectLockResult> => {
    const result = await callLockRpc('acquire_project_lock', {
        p_project_id: projectId,
        p_label: label ?? null,
    }).catch((e) => ({ error: e }));

    if ('error' in result) {
        console.warn('[projectLock] Sperre konnte nicht genommen werden:', result.error);
        return FAIL_OPEN;
    }

    const row = result.row;
    const lock = mapLockRow(row);
    // Keine Zeile trotz erfolgreichem Aufruf: die Sperre war im selben Moment
    // weg. Nichts haelt den Aufrufer auf.
    if (!lock) return FAIL_OPEN;
    return { acquired: row?.acquired === true, lock };
};

/**
 * Zeitlimit fuer das Anfordern der Sperre beim Oeffnen eines Projekts.
 *
 * Warum 4 Sekunden: das Anfordern liegt VOR dem Laden des Projekts (sonst
 * liefe die Bild-Selbstheilung in einem Projekt an, das wir nur ansehen
 * duerfen) — es verzoegert also sichtbar jeden Oeffnen-Klick. Ein RPC-Aufruf
 * gegen Supabase liegt normal weit unter einer halben Sekunde; 4 s lassen auch
 * einem langsamen Mobilfunknetz Luft, ohne dass jemand glaubt, die App haenge.
 * Genau die Faelle, in denen mehr Geduld nichts brachte — offline, nicht
 * ausgefuehrte Migration (`PGRST202`), toter Socket —, laufen ohnehin in
 * einen Fehler oder in dieses Limit.
 *
 * Beim Ueberschreiten wird NICHT abgebrochen, sondern fail-open geoeffnet:
 * lieber zwei Bearbeiter als ein blockiertes Projekt. Der spaete RPC laeuft im
 * Hintergrund zu Ende und setzt die Sperre trotzdem — sein Ergebnis
 * interessiert nur niemanden mehr.
 */
export const LOCK_ACQUIRE_TIMEOUT_MS = 4_000;

/**
 * `acquireProjectLock` mit hartem Zeitlimit.
 *
 * Kein `Promise.race` mit einem `reject`: der Verlierer eines Race bleibt
 * unbehandelt und landet als unhandled rejection im Protokoll. Hier gewinnt
 * schlicht, wer zuerst `resolve` ruft.
 */
export const acquireProjectLockWithin = (
    projectId: string,
    label?: string | null,
    timeoutMs: number = LOCK_ACQUIRE_TIMEOUT_MS,
): Promise<ProjectLockResult> =>
    new Promise<ProjectLockResult>((resolve) => {
        let done = false;
        const finish = (result: ProjectLockResult) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            resolve(result);
        };
        const timer = setTimeout(() => {
            console.warn('[projectLock] Sperre nicht rechtzeitig erhalten — wird ohne Sperre geoeffnet');
            finish(FAIL_OPEN);
        }, timeoutMs);
        // acquireProjectLock wirft nicht; der catch ist der Guertel zum
        // Hosentraeger, damit ein kuenftiger Umbau dort hier nicht haengen bleibt.
        acquireProjectLock(projectId, label).then(finish, () => finish(FAIL_OPEN));
    });

/**
 * Herzschlag. Bewusst derselbe RPC — das erneute Anfordern DURCH DENSELBEN
 * Nutzer IST der Herzschlag. Ein eigener Weg dafuer waere eine zweite Stelle,
 * an der die Ablaufregel steht.
 *
 * Der Rueckgabewert ist nicht bloss Zierde: liefert er `acquired: false`, hat
 * inzwischen jemand anderes uebernommen (Admin-Uebernahme oder Ablauf nach
 * einem laengeren Netzausfall) — die Oberflaeche kann dann umschalten.
 */
export const renewProjectLock = (
    projectId: string,
    label?: string | null,
): Promise<ProjectLockResult> => acquireProjectLock(projectId, label);

/**
 * Gibt die EIGENE Sperre frei. Fremde Zeilen ruehrt die RPC nicht an.
 *
 * Liefert true, wenn tatsaechlich etwas freigegeben wurde. false ist kein
 * Fehler — die Sperre war abgelaufen oder wurde aufgebrochen.
 */
export const releaseProjectLock = async (projectId: string): Promise<boolean> => {
    try {
        const { data, error } = await supabase.rpc('release_project_lock', {
            p_project_id: projectId,
        });
        if (error) {
            console.warn('[projectLock] Sperre konnte nicht freigegeben werden:', error);
            return false;
        }
        return data === true;
    } catch (e) {
        // Beim Verlassen der Seite ist ein abgebrochener Aufruf der Normalfall.
        // Die Sperre verfaellt ohnehin nach LOCK_STALE_MS.
        console.warn('[projectLock] Sperre konnte nicht freigegeben werden:', e);
        return false;
    }
};

/**
 * Bricht eine fremde Sperre auf (nur Administratoren; die Pruefung steht in
 * der RPC, nicht hier).
 *
 * BEWUSSTE ABWEICHUNG vom Fail-open: Ein Fehler liefert `acquired: false`.
 * Das Fail-open oben schuetzt das ARBEITEN — hier will jemand aktiv etwas
 * wegnehmen, und ein gescheitertes Aufbrechen als Erfolg zu melden hiesse, den
 * anderen Bearbeiter zu verschweigen. Geworfen wird trotzdem nichts.
 */
export const takeoverProjectLock = async (
    projectId: string,
    label?: string | null,
): Promise<ProjectLockResult> => {
    const result = await callLockRpc('takeover_project_lock', {
        p_project_id: projectId,
        p_label: label ?? null,
    }).catch((e) => ({ error: e }));

    if ('error' in result) {
        console.warn('[projectLock] Uebernahme fehlgeschlagen:', result.error);
        return { acquired: false, lock: null };
    }

    const lock = mapLockRow(result.row);
    return { acquired: lock !== null, lock };
};

/**
 * Liest die geltende Sperre, ohne sie zu nehmen — fuer Listen und fuer das
 * Aktualisieren des Banners. Lesen laeuft ueber die Tabelle (RLS-Policy
 * `project_locks_select_project_or_admin`), nicht ueber eine RPC.
 *
 * Kein Treffer und Lesefehler liefern beide null: „keine Sperre bekannt".
 */
export const fetchProjectLock = async (projectId: string): Promise<ProjectLock | null> => {
    try {
        const { data, error } = await supabase
            .from('project_locks')
            .select('project_id, user_id, user_label, acquired_at, heartbeat_at')
            .eq('project_id', projectId)
            .maybeSingle();
        if (error) {
            console.warn('[projectLock] Sperre konnte nicht gelesen werden:', error);
            return null;
        }
        return mapLockRow(data);
    } catch (e) {
        console.warn('[projectLock] Sperre konnte nicht gelesen werden:', e);
        return null;
    }
};

/**
 * ═══ Sperren fuer eine ganze LISTE ═════════════════════════════════════════
 *
 * Die Projektuebersicht will an jeder Zeile wissen, ob dort jemand drinsitzt.
 * Eine Abfrage je Zeile waeren bei 200 Projekten 200 Anfragen — deshalb EIN
 * `in`-Filter je Haeppchen (`LOCK_LIST_CHUNK_SIZE`), gedeckelt auf
 * `LOCK_LIST_MAX_PROJECTS`.
 *
 * Gelesen wird die Tabelle direkt; die RLS-Policy
 * `project_locks_select_project_or_admin` liefert ohnehin nur Zeilen zu
 * Projekten, die der Betrachter sehen darf. Was er nicht sehen darf, fehlt in
 * der Antwort — und fehlt damit auch im Ergebnis, ohne Sonderbehandlung.
 */

/**
 * Ein einziges Mal warnen, dann Ruhe.
 *
 * Anders als beim geoeffneten Projekt laeuft diese Abfrage bei JEDEM Aufbau
 * der Liste und danach alle 45 s. Ist Migration Nr. 33 noch nicht gefahren
 * (oder ist der Rechner offline), schriebe jede Runde dieselbe Warnung in die
 * Konsole und begruebe darunter alles, was dort wirklich zu sehen sein soll.
 * Der Zustand aendert sich nicht dadurch, dass man ihn hundertmal meldet.
 */
let listReadFailureLogged = false;

/**
 * Liest die geltenden Sperren zu mehreren Projekten auf einmal.
 *
 * FAIL-OPEN wie alles hier: ein Fehler liefert die bis dahin eingesammelten
 * Zeilen (im Regelfall also eine leere Map) und niemals eine Ausnahme. Ohne
 * Sperrinformation sieht die Liste exakt so aus wie vorher — der Hinweis ist
 * Beiwerk und darf die Projektliste unter keinen Umstaenden aufhalten.
 *
 * Der Schluessel der Map ist die Projektkennung; abgelaufene Zeilen werden
 * NICHT herausgefiltert. Das entscheidet die Anzeige ueber
 * `describeProjectLockBadge` — mit ihrer eigenen Uhr, die zwischen zwei
 * Abfragen weiterlaeuft.
 */
export const fetchProjectLocks = async (
    projectIds: ReadonlyArray<string | null | undefined>,
): Promise<Map<string, ProjectLock>> => {
    const result = new Map<string, ProjectLock>();
    const ids = pickLockQueryIds(projectIds);
    if (ids.length === 0) return result;

    for (const chunk of chunkLockQueryIds(ids)) {
        try {
            const { data, error } = await supabase
                .from('project_locks')
                .select('project_id, user_id, user_label, acquired_at, heartbeat_at')
                .in('project_id', chunk);
            if (error) {
                if (!listReadFailureLogged) {
                    listReadFailureLogged = true;
                    console.warn('[projectLock] Sperren der Liste nicht lesbar — Hinweis bleibt aus:', error);
                }
                return result;
            }
            for (const row of data || []) {
                const lock = mapLockRow(row);
                if (lock) result.set(lock.projectId, lock);
            }
        } catch (e) {
            if (!listReadFailureLogged) {
                listReadFailureLogged = true;
                console.warn('[projectLock] Sperren der Liste nicht lesbar — Hinweis bleibt aus:', e);
            }
            return result;
        }
    }

    return result;
};
